import i18n from '@/i18n';

/**
 * Get a user-friendly error message for API errors
 * @param {Error} error - The error object from API response
 * @returns {string} - User-friendly error message
 */
export const getApiErrorMessage = (error) => {
  // Check if it's a network error
  if (!error.response) {
    return i18n.t('common.network_error');
  }

  const status = error.response.status;
  const toast = error.response.data?.toast;
  const message = error.response.data?.message;
  const code = error.response.data?.code;

  // Handle specific status codes
  switch (status) {
    case 400:
      // Don't show toast for reCAPTCHA errors as they're handled separately
      if (message?.toLowerCase().includes('recaptcha') || code?.toLowerCase().includes('entity_not_found')) {
        return null; // Return null to indicate no toast should be shown
      }
      return toast || i18n.t('common.api_error');
    
    case 401:
      // Don't show toast for auth errors as they're handled by redirect
      return null;
    
    case 403:
      return toast || 'Access denied';
    
    case 404:
      return toast || 'Resource not found';
    
    case 429:
      return toast || 'Too many requests. Please try again later.';
    
    case 500:
    case 502:
    case 503:
    case 504:
      return i18n.t('common.server_error');
    
    default:
      return toast || i18n.t('common.api_error');
  }
};

/**
 * Check if an error should show a toast notification
 * @param {Error} error - The error object from API response
 * @returns {boolean} - True if toast should be shown
 */
export const shouldShowErrorToast = (error) => {
  // Don't show toast for network errors during token refresh
  if (error.config?.url?.includes('/auth/token/refresh')) {
    return false;
  }

  // Don't show toast for login/signup errors (handled by forms)
  if (error.config?.url?.includes('/auth/login') || 
      error.config?.url?.includes('/auth/signup')) {
    return false;
  }

  // Don't show toast for reCAPTCHA errors (handled separately)
  if (error.response?.status === 400 && 
      error.response?.data?.message?.toLowerCase().includes('recaptcha')) {
    return false;
  }

  // Don't show toast for 401 errors (handled by redirect)
  if (error.response?.status === 401) {
    return false;
  }

  return true;
};
